import { useMemo } from 'react'
import { LineItem } from '@/hooks/useDocumentInvoiceData'
import { useGetPODiscrepancies } from '@/service-library/hooks/po-discrepancies'
import { PDPurchaseOrder, POLineItemMatch } from '@/types/po-match'
import {
  buildDiscrepancyCheckPayload,
  mapDiscrepancyResponse,
} from '@/utils/po-discrepancies'

type UsePODiscrepanciesOptions = {
  id: string
  purchaseOrderNumber: string | null
  vendorId: string | null
  lineItems: LineItem[]
  purchaseOrders: PDPurchaseOrder[]
  lineItemMatches: POLineItemMatch[]
  enabled: boolean
}

/**
 * Thin orchestration hook for the PO discrepancy check: builds the ad-hoc
 * check payload from data the view already holds (fast path — the server
 * does no document or relay lookups), fetches, and memoizes the pure UI
 * mapping. The payload is part of the query key, so match, PO, vendor, and
 * PO number changes all re-run the check automatically.
 */
export default function usePODiscrepancies({
  id,
  purchaseOrderNumber,
  vendorId,
  lineItems,
  purchaseOrders,
  lineItemMatches,
  enabled,
}: UsePODiscrepanciesOptions) {
  const payload = useMemo(() => {
    return buildDiscrepancyCheckPayload({
      invoice: { id, purchaseOrderNumber, vendorId, lineItems },
      purchaseOrders,
      lineItemMatches,
    })
  }, [
    id,
    purchaseOrderNumber,
    vendorId,
    lineItems,
    purchaseOrders,
    lineItemMatches,
  ])

  const query = useGetPODiscrepancies({
    payload,
    enabled,
    // Keep showing the previous discrepancies while a payload change refetches,
    // instead of flashing them away until the new result lands.
    placeholderData: (previousData) => previousData,
  })

  const discrepancies = useMemo(
    () => mapDiscrepancyResponse(query.data, purchaseOrders),
    [query.data, purchaseOrders],
  )

  return {
    ...query,
    discrepancies,
  }
}
